import { useState, useMemo } from "react";
import {
  useMutation,
  useInfiniteQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { AssistantsService } from "@/generated";
import { DATA_KEYS } from "@/utils/useRemoteData";
import { useSpotUser } from "@/utils/spotUser";
import { SpotToast } from "@/components/SpotToast";
import BaseDialog from "../Modals/BaseDialog";
import {
  AssistantVersionContext,
  AssistantVersionInput,
} from "../Form/AssistantVersionField";
import { ExportPreviewDialog } from "./ExportPreviewDialog";
import { useRouter } from "next/navigation";

const MAX_VERSIONS_PAGE = 10;

interface AssistantVersionActionDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  assistantId: string;
  assistantName: string;
  action: "duplicate" | "export";
}

export const AssistantVersionActionDialog = ({
  isOpen,
  onOpenChange,
  assistantId,
  assistantName,
  action,
}: AssistantVersionActionDialogProps) => {
  const [selectedVersionId, setSelectedVersionId] = useState<string | null>(
    "current"
  );
  const { spotGroup } = useSpotUser();
  const queryClient = useQueryClient();
  const router = useRouter();

  // All hooks must be called unconditionally (before any returns)
  const { data } = useInfiniteQuery({
    queryKey: [DATA_KEYS.ASSISTANT_VERSIONS, assistantId, spotGroup.id],
    queryFn: ({ pageParam = 1 }) =>
      AssistantsService.getAssistantVersions({
        path: {
          group_id: spotGroup.id,
          assistant_id: assistantId,
        },
        query: {
          page: pageParam,
          size: MAX_VERSIONS_PAGE,
        },
      }),
    enabled: isOpen && !!assistantId && action === "duplicate",
    placeholderData: keepPreviousData,
    initialPageParam: 1,
    getPreviousPageParam: (firstPage) => {
      if (!firstPage.data.page) return;
      return firstPage.data.page > 1 ? firstPage.data.page - 1 : undefined;
    },
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.data.page || !lastPage.data.pages) return;

      return lastPage.data.page < lastPage.data.pages
        ? allPages.length + 1
        : undefined;
    },
    refetchOnMount: false,
    refetchOnWindowFocus: false,
  });

  const versions = useMemo(
    () => data?.pages?.flatMap((page) => page.data.items),
    [data?.pages]
  );

  const duplicateAssistantMutation = useMutation({
    mutationFn: AssistantsService.duplicateAssistant,
    onSuccess: ({ data: { id } }) => {
      SpotToast.success("Agent duplicated");
      queryClient.invalidateQueries({
        queryKey: [DATA_KEYS.ASSISTANTS],
      });
      router.push(`/assistant/${id}`);
      onOpenChange(false);
    },
    onError: () => {
      SpotToast.error("Error duplicating the agent");
    },
  });

  const handleAction = () => {
    const versionId =
      selectedVersionId === "current" ? undefined : selectedVersionId;

    const baseParams = {
      path: {
        group_id: spotGroup.id,
        assistant_id: assistantId,
      },
    };

    const params = versionId
      ? { ...baseParams, query: { version_id: versionId } }
      : baseParams;

    duplicateAssistantMutation.mutate(params);
  };

  const isLoading_ = duplicateAssistantMutation.status === "pending";

  // For export, use the new ExportPreviewDialog
  if (action === "export") {
    return (
      <ExportPreviewDialog
        key={assistantId}
        isOpen={isOpen}
        onOpenChange={onOpenChange}
        entityId={assistantId}
        entityName={assistantName}
        entityType="assistant"
      />
    );
  }

  // For duplicate, keep the original behavior
  return (
    <BaseDialog
      isOpen={isOpen}
      onOpenChange={onOpenChange}
      title="Duplicate agent"
      description={`Select which version of "${assistantName}" you want to duplicate.`}
      footerActions={[
        {
          label: "Cancel",
          props: {
            variant: "outline",
            onClick: () => onOpenChange(false),
            disabled: isLoading_,
          },
        },
        {
          label: isLoading_ ? "Processing..." : "Duplicate",
          props: {
            onClick: handleAction,
            disabled: isLoading_,
          },
        },
      ]}
    >
      <div className="space-y-4">
        <AssistantVersionContext.Provider value={assistantId}>
          <AssistantVersionInput
            value={selectedVersionId || undefined}
            onChange={setSelectedVersionId}
            allowCurrentVersion
            disableDeselection
            disablePortal
          />
        </AssistantVersionContext.Provider>
      </div>
    </BaseDialog>
  );
};
